import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { DsBadge, DsButton, DsSpinner } from "../design-system";
import { assetService } from "../services/api";
import {
  fieldLabel,
  inputSm,
} from "../components/Admin/assets/constants";

export default function MyAssets() {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    categoryId: "",
    typeId: "",
    notes: "",
    neededBy: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        assetService.listMyAssets(),
        assetService.listMyRequests(),
      ]);
      const aList = aRes?.data?.assets || aRes?.data || aRes?.assets || [];
      const rList = rRes?.data?.requests || rRes?.data || rRes?.requests || [];
      setAssets(Array.isArray(aList) ? aList : []);
      setRequests(Array.isArray(rList) ? rList : []);
    } catch (err) {
      setAssets([]);
      setRequests([]);
      toast.error(err?.response?.data?.message || "Could not load My Assets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Please describe what you need");
      return;
    }
    setSaving(true);
    try {
      await assetService.createMyRequest({
        ...form,
        reason: form.title || form.notes,
        remarks: form.notes,
      });
      toast.success("Request submitted");
      setShowForm(false);
      setForm({ title: "", categoryId: "", typeId: "", notes: "", neededBy: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Request failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <DsSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="ds-h1 text-[hsl(var(--ds-fg))]">My Assets</h1>
          <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
            Assets assigned to you and your request status.
          </p>
        </div>
        <DsButton type="button" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Close form" : "Request asset"}
        </DsButton>
      </div>

      {showForm ? (
        <form
          onSubmit={submitRequest}
          className="max-w-lg space-y-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-4"
        >
          <div>
            <label className={fieldLabel}>What do you need?</label>
            <input
              className={inputSm}
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              required
            />
          </div>
          <div>
            <label className={fieldLabel}>Needed by</label>
            <input
              type="date"
              className={inputSm}
              value={form.neededBy}
              onChange={(e) => setForm((f) => ({ ...f, neededBy: e.target.value }))}
            />
          </div>
          <div>
            <label className={fieldLabel}>Notes</label>
            <textarea
              className={inputSm}
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <DsButton type="submit" disabled={saving}>
            {saving ? "Submitting…" : "Submit request"}
          </DsButton>
        </form>
      ) : null}

      <section className="space-y-2">
        <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">Assigned to me</h2>
        <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
          {assets.length === 0 ? (
            <p className="ds-caption p-4 text-[hsl(var(--ds-muted-fg))]">
              No assets currently assigned.
            </p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--ds-border))]">
              {assets.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <p className="font-medium text-[hsl(var(--ds-fg))]">{a.name}</p>
                    <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                      {a.tag || a.assetTag || a.asset_tag || "—"}
                      {a.serialNumber || a.serial_number
                        ? ` · SN ${a.serialNumber || a.serial_number}`
                        : ""}
                    </p>
                  </div>
                  <DsBadge>{a.status || "assigned"}</DsBadge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">My requests</h2>
        <div className="overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]">
          {requests.length === 0 ? (
            <p className="ds-caption p-4 text-[hsl(var(--ds-muted-fg))]">
              No requests yet.
            </p>
          ) : (
            <ul className="divide-y divide-[hsl(var(--ds-border))]">
              {requests.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                  <div>
                    <p className="font-medium text-[hsl(var(--ds-fg))]">
                      {r.title || r.requestNo || `#${r.id}`}
                    </p>
                    <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                      {(r.createdAt || r.created_at || "").slice(0, 10) || "—"}
                    </p>
                  </div>
                  <DsBadge>{r.status || "pending"}</DsBadge>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
